import React, { ChangeEvent, FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutsideClickHook } from '~/src/hooks/use-outside-click.hook';
import Textbox from '~/src/components/form-components/textbox/textbox';
import { DropdownListMemo } from '~/src/components/common-components/dropdown-list/dropdown-list';
import { Portal } from '~/src/components/common-components/portal/portal';
import { useAutoUpdate } from '~/src/hooks/use-auto-update.hook';
import { useDebounceHook } from '~/src/hooks/use-debounce.hook';
import { notFoundIndex, StrategyEnum } from '~/src/components/form-components/dropdown/dropdown.constants';
import { updateCoords } from './dropdown.utils';
import type {
    CoordsType,
    DropdownListItemInterface,
    DropdownProps,
} from '~/src/components/form-components/dropdown/dropdown.types';
import * as Styled from './dropdown.styles';

/**
 * Used in Module Federation
 */
const Dropdown: FC<DropdownProps> = ({
    value = '',
    defaultValue = '',
    mutableDefaultValue = false,
    name,
    error,
    required,
    onClear,
    onValueChange,
    validated,
    label,
    placeholder,
    options = [],
    optionsDisplayLimit,
    onSuggestedValueChange,
    withoutSuggest = true,
    isReadOnly = false,
    emptyOutputData,
    className,
    onBlurHandler,
    notificationData,
    analyticsref,
    analyticsreferror,
    autoComplete = 'off',
    sendErrorAnalytics,
    inputErrorRef,
    autocompleteWithSelect,
    needCheckValue,
    descriptionText,
    isSearchIcon,
    hasNeedClear,
    isHideCloseIcon,
    withOptionsDisplayIcon,
    noResetWithForm,
    backgroundColor,
    strategy = StrategyEnum.PAGE,
    'data-testid': dataTestId = 'dropdown',
}) => {
    const [isOpened, setIsOpened] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const [coords, setCoords] = useState<CoordsType>({
        top: 0,
        left: 0,
        width: 0,
    });
    const debouncedLocalValue = useDebounceHook(localValue, 1500);
    const dropdownRef: RefObject<HTMLDivElement> = useRef(null);
    const textBoxRef: RefObject<HTMLDivElement> = useRef(null);
    const allOptions = useRef(options);
    const defaultValueIsSet = useRef(false);

    useAutoUpdate(isOpened, () => updateCoords(textBoxRef, strategy, setCoords, inputErrorRef), textBoxRef, [
        isOpened,
        error,
    ]);

    useOutsideClickHook({ ref: dropdownRef, noTargetRefArray: [textBoxRef], callback: () => setIsOpened(false) });

    useEffect(() => {
        onValueChange && onValueChange(localValue);
    }, [localValue]);

    useEffect(() => {
        if (!value) {
            setLocalValue('');
            return;
        }

        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        if (!defaultValue || defaultValueIsSet?.current) {
            return;
        }

        const finalOptions = getOptions();
        const defaultOptionIndex = finalOptions.findIndex((option) => {
            if (typeof option === 'string') {
                return option === defaultValue;
            }

            return option.value === defaultValue;
        });

        if (defaultOptionIndex > notFoundIndex) {
            const defaultOption = finalOptions[defaultOptionIndex];
            const selectedValue = typeof defaultOption === 'string' ? defaultOption : defaultOption.value;

            onSuggestHandler(selectedValue, defaultOptionIndex);
        } else {
            setLocalValue(defaultValue);
        }

        if (!mutableDefaultValue) {
            defaultValueIsSet.current = true;
        }
    }, [defaultValue]);

    useEffect(() => {
        hasNeedClear && clearField();
    }, [hasNeedClear]);

    const getOptions = () => (allOptions.current?.length ? allOptions.current : options);

    const optionsModified = useMemo(() => {
        if (emptyOutputData?.length && !options.length) {
            return emptyOutputData;
        }

        if (optionsDisplayLimit) {
            return options.slice(0, optionsDisplayLimit);
        }

        return options;
    }, [options]);

    useEffect(() => {
        if (
            emptyOutputData?.length &&
            !options.length &&
            sendErrorAnalytics &&
            !isReadOnly &&
            (value || debouncedLocalValue)
        ) {
            sendErrorAnalytics();
        }
    }, [debouncedLocalValue, isReadOnly]);

    const getOptionId = (optionsExternal: DropdownListItemInterface | string) => {
        return typeof optionsExternal === 'string' ? '' : optionsExternal.id;
    };

    const getFiasId = (optionsExternal: DropdownListItemInterface | string) => {
        return typeof optionsExternal === 'string' ? '' : optionsExternal.houseFiasId;
    };

    const onSuggestHandler = useCallback(
        (suggestValue: string, index: number) => {
            const selectedOption = options[index] || allOptions.current[index];
            const optionId = getOptionId(selectedOption);
            const optionFiasId = getFiasId(selectedOption);
            if (suggestValue) {
                onSuggestedValueChange &&
                    onSuggestedValueChange({
                        elem: dropdownRef.current,
                        name,
                        suggestValue,
                        value: localValue,
                        index,
                        id: optionId,
                        houseFiasId: optionFiasId,
                    });
            }
            setLocalValue(suggestValue);
            setIsOpened(false);
        },
        [optionsModified],
    );

    const onFocusHandler = () => {
        !withoutSuggest && setIsOpened(true);
    };

    const setOpenedState = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target instanceof Node) {
            const isClickInsideDropdown = dropdownRef.current?.contains(e.target);
            if (!isClickInsideDropdown) {
                setIsOpened((prev) => !prev);
            }
        }
    };

    const clearField = () => {
        setLocalValue('');
        onClear && onClear();
    };

    const onValueChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        !withoutSuggest && setLocalValue(event.target.value || '');
        onValueChange && onValueChange(event.target.value || '');
        setIsOpened(true);
    };

    const checkIsClickable = useCallback(
        (index: number) => {
            if (withoutSuggest) {
                return true;
            }
            const optionsModifiedCopy = [...optionsModified] as DropdownListItemInterface[];
            return !optionsModifiedCopy[index]?.isDefaultCursor;
        },
        [withoutSuggest, optionsModified],
    );

    const getIsShowDropDownList = () => {
        return Boolean(
            isOpened && !!optionsModified?.length && (localValue || value || withoutSuggest || autocompleteWithSelect),
        );
    };

    return (
        <Textbox
            label={label}
            value={localValue}
            required={required}
            error={error}
            notificationData={notificationData}
            placeholder={placeholder}
            name={name}
            onFocusHandler={onFocusHandler}
            onBlurHandler={onBlurHandler}
            onClear={clearField}
            onValueChange={onValueChangeHandler}
            validated={validated}
            hasDropdownStyle={withoutSuggest}
            isSelectMode={!autocompleteWithSelect}
            isOpenedDropdown={isOpened}
            isReadOnly={isReadOnly}
            onDropdownOpen={setOpenedState}
            textBoxRef={textBoxRef}
            className={className}
            analyticsref={analyticsref}
            analyticsreferror={analyticsreferror}
            autoComplete={autoComplete}
            inputErrorRef={inputErrorRef}
            needCheckValue={needCheckValue}
            descriptionText={descriptionText}
            isSearchIcon={isSearchIcon}
            isHideCloseIcon={isHideCloseIcon}
            withOptionsDisplayIcon={withOptionsDisplayIcon}
            noResetWithForm={noResetWithForm}
            backgroundColor={backgroundColor}
            data-testid={dataTestId}
        >
            <Portal elementRef={textBoxRef}>
                <Styled.DropdownList
                    isVisible={isOpened && !!optionsModified?.length}
                    ref={dropdownRef}
                    strategy={strategy}
                    style={{
                        top: `${coords.top}px`,
                        left: `${coords.left}px`,
                        width: `${coords.width}px`,
                    }}
                >
                    {getIsShowDropDownList() && (
                        <DropdownListMemo
                            onSuggestHandler={onSuggestHandler}
                            options={optionsModified}
                            checkIsClickable={checkIsClickable}
                        />
                    )}
                </Styled.DropdownList>
            </Portal>
        </Textbox>
    );
};

export default Dropdown;
